import React, { useEffect, useRef, useState } from 'react';
import { DatePicker, Result } from 'antd';
import Loader from 'Common/Loader';
import { Card } from 'react-bootstrap';
import DataGrid, { Column, FilterRow, HeaderFilter, Pager, Paging } from 'devextreme-react/cjs/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { getAllOldGoldPurchaseRateData } from '../API_Modal/MasterAPI/OldGoldPurchaseRateAPIModal';
import moment from 'moment';

const OldGoldPurchaseRateMasterMain: React.FC = () => {
    const [loading] = useState(false);
    const [permissionDetails, setPermissionDetails] = useState<string[]>([]);
    const dataGridRef = useRef<any>(null);
    const [selectedDateRange, setSelectedDateRange] = useState<any>(null);
    const [pagination, setPagination] = useState({
        pageSize: 10,
        pageNumber: 1,
    });
    const dataGridFilter = {
        showFilterRow: true,
        showHeaderFilter: true,
        currentFilter: 'auto' as 'auto' | 'onClick',
    };
    

    useEffect(() => {
        const authUserString = localStorage.getItem("authPOSUser");
        if (authUserString) {
            const authUser = JSON.parse(authUserString);
            const permissionData = authUser?.permissions || [];
            setPermissionDetails(permissionData);
        }
    }, []);

    const HasPermission = (permission: string): boolean => {
        return permissionDetails.includes(permission);
    };

    const dataSource = React.useMemo(() => {
        return new CustomStore({
            key: 'id',
            load: async (loadOptions) => {
                try {
                    const params = new URLSearchParams();
                    params.set('skip', String(loadOptions.skip ?? 0));
                    params.set('take', String(loadOptions.take ?? 10));
                    params.set('sort', JSON.stringify(loadOptions.sort ?? []));
                    params.set('filter', JSON.stringify(loadOptions.filter ?? null));
                    params.set('searchExpr', JSON.stringify(loadOptions.searchExpr ?? null));
                    params.set('searchOperation', loadOptions.searchOperation ?? '');
                    params.set('searchValue', loadOptions.searchValue ?? '');
                    const startDate = selectedDateRange?.[0]?.format('YYYY-MM-DD') || "";
                    const endDate = selectedDateRange?.[1]?.format('YYYY-MM-DD') || "";
                    const response = await getAllOldGoldPurchaseRateData(startDate, endDate, params);
                    if (!response?.data || response.data.length === 0) {
                        return { data: [], totalCount: 0 };
                    }
                    return response;
                } catch (error) {
                    console.error("Data loading error:", error);
                    return { data: [], totalCount: 0 };
                }
            },
        });
    }, [selectedDateRange]);

    const onPagingChange = ({ pageSize, pageIndex, }: { pageSize?: number; pageIndex?: number; }) => {
        setPagination({
            pageSize: pageSize ?? pagination.pageSize,
            pageNumber: (pageIndex ?? pagination.pageNumber - 1) + 1,
        });
    };

    return (
        <React.Fragment>
            {loading && <Loader />}
            <div className="page-content">
                <div className="container-fluid">
                    <Card>
                        <Card.Header className='d-flex justify-content-between align-items-center'>
                            <h5 className='m-0'>Old Gold Purchase Rate Master</h5>
                        </Card.Header>
                        <Card.Body className="pt-0">
                            {HasPermission("OldGoldPurchaseRate.Read") ? (
                                <>
                                    <div className="d-flex justify-content-start mb-2">
                                        <DatePicker.RangePicker
                                            format="YYYY-MM-DD"
                                            value={selectedDateRange}
                                            onChange={(dates) => {
                                                if (dates) {
                                                    setSelectedDateRange([moment(dates[0]?.toISOString()), moment(dates[1]?.toISOString())]);
                                                } else {
                                                    setSelectedDateRange(null);
                                                }
                                                dataGridRef.current?.instance?.refresh();
                                            }}
                                            getPopupContainer={() => document.body}
                                        />
                                    </div>
                                    <DataGrid
                                        ref={dataGridRef}
                                        dataSource={dataSource}
                                        remoteOperations={{ paging: true }}
                                        onOptionChanged={(e) => {
                                            if (e.fullName === 'paging.pageIndex' || e.fullName === 'paging.pageSize') {
                                                onPagingChange({
                                                    pageIndex: e.fullName === 'paging.pageIndex' ? e.value : pagination.pageNumber - 1,
                                                    pageSize: e.fullName === 'paging.pageSize' ? e.value : pagination.pageSize,
                                                });
                                            }
                                        }}
                                        showRowLines={true}
                                        keyExpr="id"
                                        showBorders={true}
                                    >
                                        <Pager
                                            visible={true}
                                            showPageSizeSelector={true}
                                            showNavigationButtons={true}
                                            displayMode="full"
                                            showInfo={true}
                                        />
                                        <Paging pageSize={pagination?.pageSize} />
                                        <FilterRow visible={dataGridFilter?.showFilterRow} applyFilter={dataGridFilter?.currentFilter} />
                                        <HeaderFilter visible={dataGridFilter?.showHeaderFilter} />
                                        <Column
                                            caption="Sr.No."
                                            dataType="number"
                                            alignment="center"
                                            cellRender={(e) => {
                                                const rowIndex = e.rowIndex;
                                                const currentPage = pagination.pageNumber;
                                                const pageSize = pagination.pageSize;
                                                const serialNumber = (currentPage - 1) * pageSize + rowIndex + 1;
                                                return <span>{serialNumber}</span>;
                                            }}
                                        />
                                        <Column dataField="rate" caption="Rate" dataType="string"
                                            cellRender={({ data }) => data.rate?.toFixed(2)}
                                        />
                                        <Column
                                            dataField="fromDate"
                                            caption="From Date"
                                            dataType="string"
                                            cellRender={({ data }) => moment(data.fromDate).format('DD/MM/YYYY')}
                                        />

                                        <Column
                                            dataField="toDate"
                                            caption="To Date"
                                            dataType="string"
                                            cellRender={({ data }) => moment(data.toDate).format('DD/MM/YYYY')}
                                        />
                                    </DataGrid>
                                </>
                            ) : (

                                <div className="text-center fs-5 fw-bold">
                                    <Result
                                        status="403"
                                        title="401"
                                        subTitle="Sorry, you are not authorized to access this data."
                                        className="custom-result"
                                    />
                                </div>
                            )
                            }
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </React.Fragment>
    );
};

export default OldGoldPurchaseRateMasterMain;
